import React from "react";
import { formatNumber } from "@/lib/utils";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface NCRPrintTemplateProps {
  ncr?: any;
  data?: any;
  config?: any;
}

const SEVERITY_MAP: Record<string, { label: string; color: string }> = {
  LOW: { label: "Thấp", color: "text-slate-700" },
  MEDIUM: { label: "Trung bình", color: "text-amber-700" },
  HIGH: { label: "Cao", color: "text-orange-600 font-bold" },
  CRITICAL: { label: "Nghiêm trọng", color: "text-red-700 font-bold" },
};

const SOURCE_MAP: Record<string, string> = {
  IQC: "IQC",
  IPQC: "IPQC",
  FQC: "FQC",
  INTERNAL: "Nội bộ",
  CUSTOMER: "Khách hàng",
  SUPPLIER: "NCC",
};

const STATUS_MAP: Record<string, string> = {
  OPEN: "Mới mở",
  INVESTIGATING: "Đang điều tra",
  CORRECTIVE_ACTION: "Đang khắc phục",
  VERIFICATION: "Chờ thẩm tra",
  CLOSED: "Đã đóng",
};

export default function NCRPrintTemplate({ ncr, data, config }: NCRPrintTemplateProps) {
  const d = ncr || data || {};
  const docId = d?.ncrId || (d?.id?.length > 20 ? `NCR-${d.id.slice(0, 8)}` : d?.id) || "";
  const sev = SEVERITY_MAP[d.severity] || { label: d.severity || "—", color: "text-black" };

  const reworkCost = Number(d.reworkCost || 0);
  const scrapCost = Number(d.scrapCost || 0);
  const otherCost = Number(d.otherCost || 0);
  const totalCost = Number(d.totalHandlingCost || d.costImpact || reworkCost + scrapCost + otherCost);

  return (
    <div
      className="p-[8mm] text-black leading-tight text-[11px] bg-white w-full max-w-[210mm] mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <ISOPrintHeader config={config} />

      <PrintDocumentTitle
        title="PHIẾU XỬ LÝ SỰ KHÔNG PHÙ HỢP"
        docNo={docId}
        docNoLabel="Số phiếu"
        date={d.createdAt || d.issueDate || new Date()}
        dateLabel="Ngày lập"
      />

      {/* 1. THÔNG TIN CHUNG */}
      <div className="mb-2.5">
        <PrintInfoGrid
          labelWidth="w-[120px]"
          leftColumn={[
            { label: "Mức độ", value: <span className={sev.color}>{sev.label}</span>, isBold: true },
            { label: "Nguồn phát hiện", value: SOURCE_MAP[d.source] || d.source || "—" },
            {
              label: "Chứng từ liên quan",
              value: d.relatedEntity?.code
                ? `${d.relatedEntity.code} - ${d.relatedEntity.name || ""}`
                : d.relatedId || "—",
            },
            { label: "Bộ phận xử lý", value: d.responsibleDept || "—" },
          ]}
          rightColumn={[
            { label: "Trạng thái", value: STATUS_MAP[d.status] || d.status || "—" },
            {
              label: "Cỡ mẫu / SL lỗi",
              value: `${d.sampleSize ? `${formatNumber(d.sampleSize)} • ` : ""}${formatNumber(d.sampleNgCount || d.defectQty || 0)} ${d.unit || "SP"}`,
              isBold: true,
            },
            { label: "Chi phí lỗi", value: `${formatNumber(totalCost)} ₫`, isBold: true },
          ]}
        />
      </div>

      {/* 2. THỐNG KÊ SỐ LƯỢNG NG & CHI PHÍ XỬ LÝ */}
      <div className="border border-black mb-2.5">
        <div className="bg-slate-100 px-2 py-0.5 font-bold text-[10.5px] border-b border-black uppercase">
          I. Số lượng lỗi &amp; chi phí
        </div>
        <div className="grid grid-cols-5 gap-1.5 p-2 text-center text-[10.5px]">
          <div className="border border-slate-300 p-1 bg-slate-50">
            <div className="text-[9.5px] text-slate-600 font-bold">SL lỗi</div>
            <div className="font-bold font-mono text-rose-700">{formatNumber(d.sampleNgCount || d.defectQty || 0)}</div>
          </div>
          <div className="border border-slate-300 p-1 bg-slate-50">
            <div className="text-[9.5px] text-slate-600 font-bold">Lựa / sửa lỗi</div>
            <div className="font-bold font-mono">{formatNumber(reworkCost)} ₫</div>
          </div>
          <div className="border border-slate-300 p-1 bg-slate-50">
            <div className="text-[9.5px] text-slate-600 font-bold">Hủy / bù</div>
            <div className="font-bold font-mono">{formatNumber(scrapCost)} ₫</div>
          </div>
          <div className="border border-slate-300 p-1 bg-slate-50">
            <div className="text-[9.5px] text-slate-600 font-bold">Khác</div>
            <div className="font-bold font-mono">{formatNumber(otherCost)} ₫</div>
          </div>
          <div className="border border-rose-500 p-1 bg-rose-50">
            <div className="text-[9.5px] text-rose-800 font-bold">Tổng chi phí</div>
            <div className="font-extrabold font-mono text-rose-800">{formatNumber(totalCost)} ₫</div>
          </div>
        </div>
      </div>

      {/* 3. MÔ TẢ SỰ CỐ & NGUYÊN NHÂN */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div className="border border-black">
          <div className="bg-slate-100 font-bold text-[10.5px] px-2 py-0.5 border-b border-black uppercase">
            D2. Mô tả lỗi
          </div>
          <div className="p-2 text-[10.5px] min-h-[44px] whitespace-pre-wrap">
            {d.issueSummary || d.description || "—"}
          </div>
        </div>
        <div className="border border-black">
          <div className="bg-slate-100 font-bold text-[10.5px] px-2 py-0.5 border-b border-black uppercase">
            D3. Ngăn chặn
          </div>
          <div className="p-2 text-[10.5px] min-h-[44px] whitespace-pre-wrap">
            {d.containmentAction || "—"}
          </div>
        </div>
      </div>

      {/* 4. NGUYÊN NHÂN GỐC RỄ & CAPA */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div className="border border-black">
          <div className="bg-slate-100 font-bold text-[10.5px] px-2 py-0.5 border-b border-black uppercase">
            D4. Nguyên nhân gốc
          </div>
          <div className="p-2 text-[10.5px] min-h-[44px] whitespace-pre-wrap">
            {d.rootCause || "—"}
          </div>
        </div>
        <div className="border border-black">
          <div className="bg-slate-100 font-bold text-[10.5px] px-2 py-0.5 border-b border-black uppercase">
            D5–D7. Khắc phục &amp; phòng ngừa
          </div>
          <div className="p-2 text-[10.5px] min-h-[44px] whitespace-pre-wrap">
            {[
              (d.correctiveAction || d.actionPlan) && `D5. ${d.correctiveAction || d.actionPlan}`,
              d.preventiveAction && `D7. ${d.preventiveAction}`,
              d.capaPlan,
            ]
              .filter(Boolean)
              .join("\n") || "—"}
          </div>
        </div>
      </div>

      {/* 5. KẾT QUẢ THẨM TRA ĐÓNG NCR */}
      <div className="border border-black p-2 mb-3 bg-slate-50 text-[10.5px]">
        <strong>D8. Kết quả xử lý:</strong>{" "}
        {d.closingResult || d.effectivenessNote || "Đang theo dõi kết quả thực hiện CAPA."}
      </div>

      {/* 6. CHỮ KÝ 4 BÊN */}
      <div className="grid grid-cols-4 gap-2 mt-3 text-[10.5px] text-center break-inside-avoid">
        <div>
          <div className="font-bold uppercase">Người Phát Hiện</div>
          <div className="text-slate-500 italic text-[9.5px]">(Ký, ghi rõ họ tên)</div>
          <div className="h-14" />
          <div className="font-semibold">{d.owner?.fullName || ""}</div>
        </div>
        <div>
          <div className="font-bold uppercase">Bộ Phận Liên Quan</div>
          <div className="text-slate-500 italic text-[9.5px]">(Xác nhận xử lý)</div>
          <div className="h-14" />
          <div className="font-semibold">{d.responsibleDept || ""}</div>
        </div>
        <div>
          <div className="font-bold uppercase">Trưởng Phòng QA/QC</div>
          <div className="text-slate-500 italic text-[9.5px]">(Thẩm định CAPA)</div>
          <div className="h-14" />
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">Ban Giám Đốc</div>
          <div className="text-slate-500 italic text-[9.5px]">(Phê duyệt)</div>
          <div className="h-14" />
          <div className="font-semibold">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
